import { getUserSelectedCurrencyCode } from '@/src/utils/currency/getCurrencyCode'
import { path } from '@/src/utils/generalUtils/path'
import { getGtmPageCountry } from '@/src/utils/analytics/gtm/getGtmPageCountry'
import { useLocale } from 'next-intl'
import { usePathname } from 'next/navigation'
import { useCallback, useMemo } from 'react'
import useClevertapEvent from '../index'

interface SigninEventPayload {
    action_name: string
    action_type?: string
    email_verified?: boolean | null
    mobile_verified?: boolean | null
    action_value: 'success' | 'fail'
    error_message?: string
    response?: any
}

const useClevertapForSignin = () => {
    const { triggerEvent } = useClevertapEvent()
    const locale = useLocale()
    const pathname = usePathname()

    // Determine current_page and page_name based on pathname
    const { current_page, page_name } = useMemo(() => {
        switch (true) {
            case !pathname || pathname === '/' || pathname === `/${locale}`:
                return { current_page: 'homepage', page_name: 'homepage' }
            case pathname.includes('/itinerary'):
                return { current_page: 'itinerary', page_name: 'itinerary' }
            case pathname.includes('/flights/results'):
                return { current_page: 'flight_srp', page_name: 'flights_srp' }
            case pathname.includes('/flights'):
                return { current_page: 'flights_homepage', page_name: 'flights_homepage' }
            case pathname.includes('/signin'):
                return { current_page: 'signin_page', page_name: 'signin_page' }
            default:
                return { current_page: 'homepage', page_name: 'homepage' }
        }
    }, [locale, pathname])

    const triggerSigninEvent = useCallback(
        (name: string, payload: SigninEventPayload) => {
            const hostname = typeof window !== 'undefined' ? window.location.hostname : 'NA'
            const domain = typeof window !== 'undefined' ? path(['location', 'hostname'], window) || 'NA' : 'NA'
            const page_country = getGtmPageCountry(hostname)
            const page_currency = getUserSelectedCurrencyCode()?.toLowerCase() || 'sar'
            const page_path = pathname || '/'

            const finalPayload = {
                ...payload,
                domain,
                page_country,
                page_currency,
                page_path,
                current_page,
                page_name,
                page_language: locale
            }

            // QA Debug: Log CleverTap signin event data
            console.log('[CleverTap]' + name + ' (signin):', finalPayload)

            triggerEvent(name, finalPayload)
        },
        [triggerEvent, locale, pathname, current_page, page_name]
    )

    return {
        triggerSigninEvent
    }
}

export default useClevertapForSignin
